// ============================================================================
// STRUCTURED MENU DATA — CMS-ready. Each item supports the fields a future
// Pack Brothers management platform would edit: name, desc, price, mp (market
// price), gf (dietary), featured, image, available, seasonal.
// Content is sourced from the live River Stone site (checkle dinner menu,
// bar bites, desserts, cigars, specials). Unverified areas are marked TBD.
// ============================================================================

export const menuMeta = {
  gm: "Travis Brazil",
  execChef: "Cameron Magee",
  pastryChef: "Jane Mastroianni",
  wineListPdf: "https://www.riverstonechophouse.com/s/RSCH-Wine-List-952025.pdf",
  updated: "Sourced from live menu · July 2026",
  steakStatement:
    "All steaks are hand cut, charbroiled with house steak seasoning, basted with smoked tallow butter, and finished with house veal Demi-Glace.",
  tempGuide: [
    ["Rare", "Cool Red Center"],
    ["Medium Rare", "Warm Red Center"],
    ["Medium", "Warm Pink Center"],
    ["Medium Well", "Slight Pink Center"],
    ["Well Done", "No Pink"],
  ],
  disclaimer:
    "*Consuming raw or undercooked meats, poultry, seafood, shellfish, or eggs can increase your risk of food borne illness, especially if you have certain medical conditions. A 20% gratuity may be added to parties of 6 or more.",
  cardFee:
    "To help offset rising processing costs, a 3% convenience fee is applied to all credit and debit card payments. No convenience fee for guests paying with cash.",
};

const it = (name, desc, price, extra = {}) => ({ name, desc, price, mp: false, gf: false, featured: false, ...extra });

// ---- SECTION: DINNER ----
export const dinner = {
  label: "Dinner",
  subcats: {
    "Raw Bar": [
      it("Oysters*", "Raw / Steamed or Broiled / Cocktail Sauce / Mignonette", null, { mp: true }),
      it("Tuna Tartare*", "Avocado / Mint / Ginger / Crunchy Salt", "18"),
      it("Jumbo Shrimp Cocktail*", "Cocktail Sauce / Horseradish / Lemon", "21"),
      it("Salmon Aburi*", "Green Oak / Cucumber / Mizuna / Snow Peas / Scallion / Soy / Lime", "24"),
      it("Oysters Rockefeller", "Bacon / Onion / Cream / Spinach / Parmesan / Bread Crumbs", "MP +10", { mp: true }),
      it("Tenderloin Tartare*", "Capers / Shallots / Dijon / Smoked Yolk / Potato Chips", "21"),
      it("Beef Carpaccio*", "Aioli / Radish / Onion Caramel / Parsley / Micro Arugula", "16"),
    ],
    Starters: [
      it("Creamy Maine Lobster Stew", "Potatoes / Tomato / Cream / Lobster / Old Bay", "21"),
      it("\u201CSalt & Pepper\u201D Calamari", "Cherry Pepper Aioli", "16"),
      it("Pork Bites", "Bacon Chunks / Pork Rinds / Sweet Chili / Radish / Scallion", "18"),
      it("Chef\u2019s Cheese & Charcuterie Plate", "Half serves 2\u20133 / Whole serves 4\u20136", "15 / 28"),
      it("Philly Cheese-Steak Spring Rolls", "Cherry Pepper Relish / Horseradish Cream", "17"),
      it("Warm \u201CDeviled\u201D Crab Dip", "Focaccia Toast Points", "20", { featured: true }),
      it("Blackened Ahi Tuna*", "Remoulade / Chow-Chow", "20", { gf: true }),
      it("Smoked Gouda Arancini", "Fried Risotto Balls / Smoked Gouda / Sun-Dried Tomato Pesto / Aioli", "18"),
    ],
    Salads: [
      it("River Stone House", "Tomato / Cucumber / Red Onion / Parmesan / Green Goddess Dressing", "12", { gf: true }),
      it("Classic Caesar", "House Croutons / Parmesan", "14"),
      it("Chophouse Wedge", "Pecans / Bacon / Point Reyes Blue Cheese / Cherry Tomato / Vincotto", "16", { gf: true }),
      it("Waldorf Salad", "Red Leaf / Endive / Walnuts / Pickled Craisins / Goat Cheese / Apple / Creamy Lemon Dijon", "15", { gf: true }),
      it("Greek Salad", "Romaine / Mixed Greens / Cucumber / Red Onion / Kalamata / Cherry Tomato / Red Wine Vinaigrette / Creamy Feta", "16", { gf: true }),
    ],
    Seafood: [
      it("Scottish Salmon*", "Carolina Gold Rice Grits / Green Beans / Sauce Meuni\u00E8re", "41", { gf: true }),
      it("Fried Shrimp Platter", "Jumbo Shrimp / RSCH Fries / Chow-Chow", "34"),
      it("Jumbo Lump Crab Cake", "Carolina Peaches / Smoke / Peach Salsa / Fresno Chili / Green Salad / Lemon Vinaigrette", "38"),
      it("Tonight\u2019s Featured Fish", "Ask your server", null, { mp: true }),
    ],
    "Steaks & Chops": [
      it("28-Day In-House Dry-Aged Bone-In Ribeye*", "22 oz \u00B7 River Stone\u2019s signature, aged in-house", "89", { gf: true, featured: true }),
      it("Filet*", "8 oz", "58", { gf: true }),
      it("New York Strip*", "12 oz", "48", { gf: true }),
      it("Smoked Kansas City Strip*", "18 oz", "54", { gf: true }),
      it("Porterhouse*", "24 oz", "62", { gf: true }),
      it("Boneless Ribeye*", "17 oz", "52", { gf: true }),
      it("Wagyu, Butcher\u2019s Choice*", "Broccolini / Bordelaise", null, { mp: true, gf: true }),
      it("Grilled Pork Porterhouse", "16 oz \u00B7 Succotash / Corn Bread / Blackberry Jam / Carolina Sauce", "42"),
      it("Pan-Roasted Duck Breast*", "Spinach & Ricotta Gnocchi / Basil / Roasted Tomatoes / Patty Pan Squash / Snap Peas", "48"),
      it("18-Hour Bone-In Short Rib", "Smoked / Molasses Rub / Fingerlings / Spinach Salad / Jicama Slaw / Fig BBQ", "45", { gf: true }),
    ],
    "Surf & Turf": [
      it("Dinner for One", "Crab & lobster-topped 8 oz filet / Smashed Potatoes / Broccolini / B\u00E9arnaise", "80", { gf: true }),
      it("Dinner for Two", "Crab & lobster-topped 8 oz filet / Smashed Potatoes / Broccolini / B\u00E9arnaise", "150", { gf: true }),
    ],
    Sides: [
      it("RSCH House-Cut Fries", "", "11"),
      it("Smashed Potatoes", "", "12"),
      it("Cucumber", "Heirloom tomato & mint", "12"),
      it("Baked Spud Sundae", "", "13"),
      it("RSCH Succotash", "Squash / Bacon / Corn / Lima", "13"),
      it("Asparagus with B\u00E9arnaise", "", "12"),
      it("Mashed Sweet Potato", "Brown Sugar / Thyme", "12"),
      it("Macaroni & Six Cheeses", "Add short rib +8", "14"),
      it("Sherried Skillet Mushrooms", "", "12"),
      it("Broccolini with Brown Butter", "", "11"),
      it("Brussels Sprouts", "Bacon Onion Jam", "13"),
    ],
    "Steak Enhancements": [
      it("Classic B\u00E9arnaise", "", "4"),
      it("Point Reyes Blue Cheese Butter", "", "4"),
      it("Veal & Pork Demi-Glace", "", "5"),
      it("Au Poivre", "", "8"),
      it("Brandy Peppercorn Sauce", "", "4"),
      it("\u00C0 la Oscar", "", "15"),
      it("Shrimp Oscar", "", "17"),
      it("Lobster Oscar", "", "19"),
      it("Compose Any Steak Plate", "Smashed Potatoes / Broccolini", "15"),
    ],
  },
};

// ---- SECTION: BAR BITES ----
export const barBites = {
  label: "Bar Bites",
  subcats: {
    Appetizers: [
      it("Warm \u201CDeviled\u201D Crab Dip", "", "20"),
      it("Chef\u2019s Cheese & Charcuterie Plate", "Serves 2\u20133 / Serves 4\u20136", "15 / 28"),
      it("\u201CSalt & Pepper\u201D Calamari", "", "16"),
      it("Blackened Sirloin Bites", "", "17"),
      it("Short Rib Mac & Cheese", "", "22"),
      it("Philly Cheese-Steak Spring Rolls", "", "16"),
      it("Blackened Ahi Tuna", "", "20"),
      it("Fried Green Tomatoes", "", "18"),
      it("Loaded Baked Potato Skins", "", "15"),
      it("Jumbo Chophouse Wings", "Buffalo / Maple Chipotle / Daytona / Cherry Bourbon BBQ / Old Bay", "15"),
    ],
    Salads: [
      it("River Stone House Salad", "Tomato / Cucumber / Red Onion / Parmesan / Green Goddess", "12"),
      it("Classic Caesar", "House Croutons / Parmesan", "14"),
      it("Chophouse Wedge", "Pecans / Bacon / Point Reyes Blue Cheese / Cherry Tomato / Vincotto", "16"),
      it("Summer Salad", "Spinach / Strawberry / Avocado / Shallots / Walnuts / Cilantro Lime Vinaigrette", "15"),
      it("Charred Greek Salad", "Charred Romaine / Red Pearl Onion / Tomato Concasse / Kalamata Tapenade / Feta Mousse / Lemon Vinaigrette", "16"),
    ],
    Sandwiches: [
      it("\u00BD Pound Cheeseburger", "Pimento or Cheddar / L.T.O.", "18"),
      it("Grilled Ribeye Sandwich", "Provolone / Caramelized Onions", "17"),
      it("Crab Cake Sandwich", "Brioche Roll / L.T.O. / Remoulade", null, { mp: true }),
      it("Chicken Sandwich", "Brioche Roll / L.T.O. \u00B7 Blackened, Fried, or Grilled", "17"),
      it("Fried Green Tomato B.L.T.", "", "17"),
    ],
  },
};

// ---- SECTION: DESSERTS ----
export const desserts = {
  label: "Desserts",
  note: "All desserts prepared in-house by Pastry Chef Jane Mastroianni.",
  subcats: {
    Desserts: [
      it("German Chocolate Cake", "Chocolate Cake / Coconut Pecan Frosting / Chocolate Frosting", "15"),
      it("Vanilla Bean Cr\u00E8me Br\u00FBl\u00E9e", "Classic Egg Custard / Br\u00FBl\u00E9ed Sugar / Strawberries", "10"),
      it("Cheesecake du Jour", "", "14"),
      it("Seasonal Gelato or Sorbet", "", "10"),
      it("Chef Dessert", "Ask your server", null, { mp: true }),
    ],
    "Dessert Cocktails": [
      it("Espresso Martini", "Vanilla Vodka / Espresso Liqueur / Espresso / Simple", "12"),
      it("Chocolate Martini", "Vanilla Vodka / Cr\u00E8me de Cacao / Mozart Chocolate / Cream", "11"),
      it("Espresso Old Fashioned", "Four Roses Bourbon / Chocolate Bitters / Brown Sugar Syrup / Espresso", "14"),
      it("Nightcap", "Frangelico / Baileys / Grand Marnier / Van Gogh Espresso / Cream", "12"),
    ],
  },
};

// ---- SECTION: CIGARS ----
export const cigars = {
  label: "Cigars",
  note: "Enjoyed on our stone cigar patio. Organized by body.",
  groups: [
    { body: "Mild", items: [
      { name: "AVO #2 Classic", price: "17" },
      { name: "Romeo y Julieta 1875 Bully", price: "10" },
      { name: "Rocky Patel Edge Lite", price: "12" },
    ]},
    { body: "Mild to Medium", items: [
      { name: "Rocky Patel Vintage 90", price: "16" },
    ]},
    { body: "Medium", items: [
      { name: "Davidoff Special R", price: "24" },
      { name: "Padron 3000 Natural", price: "10" },
    ]},
    { body: "Medium to Full", items: [
      { name: "Padron 1964 Anniversary Superior Maduro", price: "17" },
      { name: "Gran Vida Maduro by Rocky Patel", price: "12" },
      { name: "Fuente Hemingway Short Story", price: "9" },
    ]},
    { body: "Full", items: [
      { name: "Padron 1926 Anniversary Series #1", price: "27" },
      { name: "Davidoff Grand Cru #2", price: "19" },
    ]},
  ],
};

// ---- SECTION: SPECIALS ----
export const specials = {
  label: "Specials",
  happyHour: {
    when: "Monday\u2013Friday \u00B7 3 PM\u20136 PM \u00B7 Lounge & Patio",
    items: [
      "$5 Domestic Beer", "$6 Well Liquors", "$8 Draft Beer",
      "$8 Select House Wine", "$8 Select Martinis", "25% Off Select Appetizers",
    ],
    note: "Raw bar items excluded from appetizer promotion.",
  },
  weekly: [
    { day: "Monday", title: "Oyster Monday", body: "Seasonal Oysters $1 each \u00B7 Charbroiled Oysters with herb compote butter $1.50 each." },
    { day: "Tuesday", title: "Prime Rib Tuesday", body: "12 oz or 16 oz Prime Rib with grilled asparagus, mashed potatoes, au jus & fresh shaved horseradish." },
    { day: "Wednesday", title: "Military Appreciation", body: "10% off food bill with entr\u00E9e purchase for Military & DOD guests." },
    { day: "Wednesday", title: "Ladies\u2019 Martini Night", body: "Special pricing on select well martinis all night." },
    { day: "Thursday", title: "Wagyu Burger Thursday", body: "Brenner Full Blood American Wagyu burgers in the bar \u2014 a new chef feature each week." },
  ],
};

// ---- SECTION: WINE & COCKTAILS (structured; full wine list is separate) ----
export const wine = {
  label: "Wine & Cocktails",
  award: "Voted an OpenTable Diners\u2019 Choice for Notable Wine List. A carefully selected, award-winning two-stem list spanning old-world classics and Virginia\u2019s own vineyards.",
  cocktails: desserts.subcats["Dessert Cocktails"],
  categories: [
    "By the Glass", "Sparkling", "Champagne", "White", "Ros\u00E9", "Pinot Noir",
    "Cabernet Sauvignon", "Red Blends", "International Reds", "Dessert Wine",
    "Bourbon & Whiskey", "Scotch", "Cognac",
  ],
};

export const menuSections = [
  { id: "dinner", label: "Dinner" },
  { id: "bar-bites", label: "Bar Bites" },
  { id: "desserts", label: "Desserts" },
  { id: "wine", label: "Wine & Cocktails" },
  { id: "cigars", label: "Cigars" },
  { id: "specials", label: "Specials" },
];
